import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Box, Typography,
  Divider, Button, IconButton, Alert, CircularProgress,
  Chip, Popover, TextField, Stack
} from '@mui/material';
import { Close, Palette, Print, Payment, Receipt, CheckCircle, WhatsApp, Email } from '@mui/icons-material';
import { useThemeModeContext } from '../hooks/useThemeMode';
import apiService from '../services/api';
import { POSThemeId, POS_THEME_LIST, POS_THEMES } from '../utils/posThemes';

interface BillItemDto {
  itemName: string;
  variantName: string | null;
  quantity: number;
  unitPrice: number;
  lineTotal: number;
}

interface BillDto {
  billId: number;
  billNumber: string;
  billDate: string;
  tableNumber: string | null;
  orderType: string;
  items: BillItemDto[];
  subTotal: number;
  discountType: string | null;
  discountValue: number;
  discountReason: string | null;
  discountAmount: number;
  taxableAmount: number;
  cgstPercent: number;
  sgstPercent: number;
  igstPercent: number;
  cgstAmount: number;
  sgstAmount: number;
  igstAmount: number;
  serviceChargePercent: number;
  serviceCharge: number;
  roundOff: number;
  grandTotal: number;
  status: string;
}

interface BillPreviewModalProps {
  open: boolean;
  onClose: () => void;
  bill: BillDto | null;
  restaurantName?: string;
  posTheme?: POSThemeId;
  onThemeChange?: (theme: POSThemeId) => void;
  onProceedToPay: () => void;
  onCancelBill?: (billId: number) => Promise<void>;
  customerPhone?: string;
}

export const BillPreviewModal: React.FC<BillPreviewModalProps> = ({
  open,
  onClose,
  bill,
  restaurantName,
  posTheme = 'classic',
  onThemeChange,
  onProceedToPay,
  onCancelBill,
  customerPhone = '',
}) => {
  const { isDark } = useThemeModeContext();
  const [printLoading, setPrintLoading] = useState<boolean>(false);
  const [cancelLoading, setCancelLoading] = useState<boolean>(false);
  const [alert, setAlert] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [themeAnchor, setThemeAnchor] = useState<null | HTMLElement>(null);
  const [phone, setPhone] = useState(customerPhone);
  const [email, setEmail] = useState('');
  const [deliveryLoading, setDeliveryLoading] = useState<'whatsapp' | 'email' | null>(null);
  const posT = POS_THEMES[posTheme];

  useEffect(() => {
    setAlert(null);
    setPhone(customerPhone);
  }, [bill, customerPhone]);

  if (!bill) return null;

  const handlePrint = async () => {
    try {
      setPrintLoading(true);
      setAlert(null);
      await apiService.reprintBill(bill.billId);
      setAlert({ type: 'success', message: 'Print command sent successfully!' });
    } catch (err: any) {
      setAlert({ type: 'error', message: 'Reprint failed' });
    } finally {
      setPrintLoading(false);
    }
  };

  const handleDelivery = async (channel: 'whatsapp' | 'email') => {
    const recipient = channel === 'whatsapp' ? phone.trim() : email.trim();
    if (!recipient) {
      setAlert({ type: 'error', message: channel === 'whatsapp' ? 'Enter a WhatsApp number first.' : 'Enter an email address first.' });
      return;
    }
    try {
      setDeliveryLoading(channel);
      setAlert(null);
      if (channel === 'whatsapp') await apiService.sendWhatsAppReceipt(bill.billId, recipient);
      else await apiService.sendEmailReceipt(bill.billId, recipient);
      setAlert({ type: 'success', message: channel === 'whatsapp' ? 'E-bill sent on WhatsApp.' : 'E-bill sent by email.' });
    } catch (err: any) {
      setAlert({ type: 'error', message: err?.response?.data?.message || `Could not send e-bill by ${channel}.` });
    } finally {
      setDeliveryLoading(null);
    }
  };

  const receiptBg = '#ffffff';
  const receiptText = '#0f172a';
  const receiptBorder = '#cbd5e1';


  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle sx={{ m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box display="flex" alignItems="center" gap={1}>
          <Receipt color="primary" />
          <Typography variant="h6" fontWeight={700}>Bill Preview</Typography>
          {bill.status === 'Paid' && (
            <Chip label="PAID" size="small" color="success" icon={<CheckCircle fontSize="inherit" />} sx={{ height: 22, fontSize: '0.65rem', fontWeight: 700 }} />
          )}
        </Box>
        <Box display="flex" alignItems="center" gap={0.75}>
          {/* POS Theme Picker */}
          {onThemeChange && (
            <>
              <IconButton
                size="small"
                title="POS Theme"
                onClick={(e) => setThemeAnchor(e.currentTarget)}
                sx={{
                  color: posTheme === 'classic' ? 'text.secondary' : posT.accent,
                  border: '1px solid',
                  borderColor: posTheme === 'classic' ? 'divider' : `${posT.accent}50`,
                  background: posTheme === 'classic' ? 'transparent' : `${posT.accent}14`,
                  borderRadius: '8px',
                }}
              >
                <Palette fontSize="small" />
              </IconButton>
              <Popover
                open={Boolean(themeAnchor)}
                anchorEl={themeAnchor}
                onClose={() => setThemeAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                PaperProps={{
                  sx: {
                    p: 1.5, borderRadius: '14px', mt: 0.5,
                    boxShadow: '0 12px 40px rgba(0,0,0,0.18)',
                    border: '1px solid rgba(0,0,0,0.06)',
                    minWidth: 210,
                  }
                }}
              >
                <Typography sx={{ fontSize: 10, fontWeight: 800, color: '#94a3b8', mb: 1.25, textTransform: 'uppercase', letterSpacing: 1 }}>
                  POS Theme
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 0.75 }}>
                  {POS_THEME_LIST.map((t) => (
                    <Box
                      key={t.id}
                      onClick={() => { onThemeChange(t.id); setThemeAnchor(null); }}
                      sx={{
                        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.75,
                        p: 1, borderRadius: '10px', cursor: 'pointer',
                        border: `2px solid ${posTheme === t.id ? t.swatch : 'transparent'}`,
                        background: posTheme === t.id ? `${t.swatch}14` : 'transparent',
                        transition: 'all 0.15s',
                        '&:hover': { background: `${t.swatch}1a`, borderColor: `${t.swatch}60` },
                      }}
                    >
                      <Box sx={{
                        width: 30, height: 30, borderRadius: '50%', background: t.swatch,
                        boxShadow: posTheme === t.id ? `0 2px 10px ${t.swatch}60` : `0 2px 6px ${t.swatch}30`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        {posTheme === t.id && (
                          <Box sx={{ width: 10, height: 10, borderRadius: '50%', background: '#fff', opacity: 0.9 }} />
                        )}
                      </Box>
                      <Typography sx={{ fontSize: 10, fontWeight: posTheme === t.id ? 700 : 500, color: posTheme === t.id ? t.swatch : '#64748b' }}>
                        {t.name}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Popover>
            </>
          )}
          <IconButton onClick={onClose} size="small">
            <Close fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ py: 2 }}>
        {/* Receipt — always white paper look */}
        <Box
          sx={{
            background: receiptBg,
            color: receiptText,
            p: 2.5,
            borderRadius: '12px',
            fontFamily: 'monospace',
            boxShadow: isDark
              ? '0 4px 20px rgba(0,0,0,0.4), 0 1px 4px rgba(0,0,0,0.2)'
              : '0 4px 12px rgba(0,0,0,0.08)',
          }}
        >
          {/* Header */}
          <Typography variant="subtitle1" fontWeight={800} align="center" sx={{ letterSpacing: 1, color: receiptText }}>
            {(restaurantName || 'RESTAURANT').toUpperCase()}
          </Typography>

          <Divider sx={{ borderStyle: 'dashed', my: 1, borderColor: receiptBorder }} />

          <Box display="flex" justifyContent="space-between" mb={0.5}>
            <Typography variant="caption" sx={{ color: receiptText }}>Bill No: <strong>{bill.billNumber}</strong></Typography>
            <Typography variant="caption" sx={{ color: receiptText }}>Date: {bill.billDate}</Typography>
          </Box>
          <Box display="flex" justifyContent="space-between" mb={1}>
            <Typography variant="caption" sx={{ color: receiptText }}>Type: {bill.orderType}</Typography>
            {bill.tableNumber && (
              <Typography variant="caption" sx={{ color: receiptText }}>Table: <strong>{bill.tableNumber}</strong></Typography>
            )}
          </Box>

          <Divider sx={{ borderStyle: 'dashed', my: 1, borderColor: receiptBorder }} />

          {/* Items header */}
          <Box display="flex" justifyContent="space-between" mb={0.5}>
            <Typography variant="caption" sx={{ flex: 2, fontWeight: 700, color: receiptText }}>Item</Typography>
            <Typography variant="caption" sx={{ flex: 0.5, textAlign: 'center', fontWeight: 700, color: receiptText }}>Qty</Typography>
            <Typography variant="caption" sx={{ flex: 0.8, textAlign: 'right', fontWeight: 700, color: receiptText }}>Rate</Typography>
            <Typography variant="caption" sx={{ flex: 1, textAlign: 'right', fontWeight: 700, color: receiptText }}>Amt</Typography>
          </Box>

          {/* Items */}
          {bill.items.map((item, idx) => (
            <Box key={idx} display="flex" justifyContent="space-between" mb={0.25}>
              <Typography variant="caption" sx={{ flex: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: receiptText }}>
                {item.itemName}
                {item.variantName && <span style={{ color: '#64748b' }}> ({item.variantName})</span>}
              </Typography>
              <Typography variant="caption" sx={{ flex: 0.5, textAlign: 'center', color: receiptText }}>{item.quantity}</Typography>
              <Typography variant="caption" sx={{ flex: 0.8, textAlign: 'right', color: receiptText }}>₹{item.unitPrice.toFixed(2)}</Typography>
              <Typography variant="caption" sx={{ flex: 1, textAlign: 'right', color: receiptText }}>₹{item.lineTotal.toFixed(2)}</Typography>
            </Box>
          ))}

          <Divider sx={{ borderStyle: 'dashed', my: 1, borderColor: receiptBorder }} />

          {/* Totals */}
          <Box display="flex" flexDirection="column" gap={0.25}>
            <Box display="flex" justifyContent="space-between">
              <Typography variant="caption" sx={{ color: receiptText }}>Subtotal</Typography>
              <Typography variant="caption" sx={{ color: receiptText }}>₹{bill.subTotal.toFixed(2)}</Typography>
            </Box>

            {bill.discountAmount > 0 && (
              <Box display="flex" justifyContent="space-between">
                <Typography variant="caption" sx={{ color: '#16a34a', fontWeight: 700 }}>
                  Discount ({bill.discountType === 'Percent' ? `${bill.discountValue}%` : 'Flat'})
                </Typography>
                <Typography variant="caption" sx={{ color: '#16a34a', fontWeight: 700 }}>
                  -₹{bill.discountAmount.toFixed(2)}
                </Typography>
              </Box>
            )}

            <Box display="flex" justifyContent="space-between">
              <Typography variant="caption" sx={{ color: receiptText, fontWeight: 700 }}>Taxable Amount</Typography>
              <Typography variant="caption" sx={{ color: receiptText, fontWeight: 700 }}>₹{bill.taxableAmount.toFixed(2)}</Typography>
            </Box>

            {bill.cgstAmount > 0 && (
              <Box display="flex" justifyContent="space-between">
                <Typography variant="caption" sx={{ color: receiptText }}>CGST ({bill.cgstPercent}%)</Typography>
                <Typography variant="caption" sx={{ color: receiptText }}>₹{bill.cgstAmount.toFixed(2)}</Typography>
              </Box>
            )}
            {bill.sgstAmount > 0 && (
              <Box display="flex" justifyContent="space-between">
                <Typography variant="caption" sx={{ color: receiptText }}>SGST ({bill.sgstPercent}%)</Typography>
                <Typography variant="caption" sx={{ color: receiptText }}>₹{bill.sgstAmount.toFixed(2)}</Typography>
              </Box>
            )}
            {bill.igstAmount > 0 && (
              <Box display="flex" justifyContent="space-between">
                <Typography variant="caption" sx={{ color: receiptText }}>IGST ({bill.igstPercent}%)</Typography>
                <Typography variant="caption" sx={{ color: receiptText }}>₹{bill.igstAmount.toFixed(2)}</Typography>
              </Box>
            )}
            {bill.serviceCharge > 0 && (
              <Box display="flex" justifyContent="space-between">
                <Typography variant="caption" sx={{ color: receiptText }}>Service Charge ({bill.serviceChargePercent}%)</Typography>
                <Typography variant="caption" sx={{ color: receiptText }}>₹{bill.serviceCharge.toFixed(2)}</Typography>
              </Box>
            )}
            {Math.abs(bill.roundOff) > 0 && (
              <Box display="flex" justifyContent="space-between">
                <Typography variant="caption" sx={{ color: receiptText }}>Round Off</Typography>
                <Typography variant="caption" sx={{ color: receiptText }}>
                  {bill.roundOff > 0 ? '+' : ''}{bill.roundOff.toFixed(2)}
                </Typography>
              </Box>
            )}

            <Divider sx={{ my: 0.5, borderColor: receiptText, opacity: 0.3 }} />

            <Box display="flex" justifyContent="space-between">
              <Typography variant="subtitle2" fontWeight={800} sx={{ color: receiptText }}>GRAND TOTAL</Typography>
              <Typography variant="subtitle2" fontWeight={800} sx={{ color: receiptText }}>₹{bill.grandTotal.toFixed(2)}</Typography>
            </Box>
          </Box>
        </Box>

        {alert && (
          <Alert severity={alert.type} onClose={() => setAlert(null)} sx={{ mt: 1.5, py: 0 }}>
            {alert.message}
          </Alert>
        )}

        <Box sx={{ mt: 2, p: 1.5, borderRadius: 2, bgcolor: isDark ? 'rgba(255,255,255,0.04)' : '#f8fafc', border: '1px solid', borderColor: 'divider' }}>
          <Typography variant="caption" sx={{ fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.8, color: 'text.secondary' }}>
            Send e-bill directly
          </Typography>
          <Stack spacing={1} sx={{ mt: 1 }}>
            <Stack direction="row" spacing={1}>
              <TextField size="small" fullWidth label="WhatsApp number" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="10-digit number" />
              <Button variant="outlined" color="success" onClick={() => handleDelivery('whatsapp')} disabled={deliveryLoading !== null} sx={{ minWidth: 92 }}>
                {deliveryLoading === 'whatsapp' ? <CircularProgress size={17} /> : <><WhatsApp sx={{ mr: .5, fontSize: 17 }} />Send</>}
              </Button>
            </Stack>
            <Stack direction="row" spacing={1}>
              <TextField size="small" fullWidth label="Email address" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="customer@example.com" type="email" />
              <Button variant="outlined" onClick={() => handleDelivery('email')} disabled={deliveryLoading !== null} sx={{ minWidth: 92 }}>
                {deliveryLoading === 'email' ? <CircularProgress size={17} /> : <><Email sx={{ mr: .5, fontSize: 17 }} />Send</>}
              </Button>
            </Stack>
          </Stack>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
          <Button
            variant="outlined"
            onClick={handlePrint}
            disabled={printLoading}
            startIcon={printLoading ? <CircularProgress size={12} /> : <Print />}
            sx={{ borderRadius: '24px', flex: 1 }}
          >
            Print Bill
          </Button>
          {bill.status !== 'Paid' && (
            <Button
              variant="contained"
              color="success"
              onClick={onProceedToPay}
              startIcon={<Payment />}
              sx={{
                borderRadius: '24px',
                flex: 1.2,
                background: 'linear-gradient(90deg, #10b981 0%, #059669 100%)',
                boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)',
              }}
            >
              Pay Now
            </Button>
          )}
        </Box>
        {onCancelBill && bill.status !== 'Paid' && (
          <Button
            variant="text"
            color="warning"
            size="small"
            fullWidth
            onClick={async () => {
              setCancelLoading(true);
              try {
                await onCancelBill(bill.billId);
              } finally {
                setCancelLoading(false);
              }
            }}
            disabled={cancelLoading}
            sx={{ borderRadius: '24px', fontSize: '0.72rem', opacity: 0.75 }}
          >
            {cancelLoading ? <CircularProgress size={12} color="inherit" /> : 'Edit Config (Cancel & Regenerate)'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
